import { type Sfx, samplesFor } from './sfx';

const STORAGE_KEY = 'graphquest.sound.v1';

type AudioContextConstructor = typeof AudioContext;

function resolveAudioContext(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null;
  const withPrefix = window as typeof window & { webkitAudioContext?: AudioContextConstructor };
  return window.AudioContext ?? withPrefix.webkitAudioContext ?? null;
}

function readStoredPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export class AudioService {
  private context: AudioContext | null = null;
  private readonly buffers = new Map<Sfx, AudioBuffer>();
  private soundEnabled = readStoredPreference();
  private unlockBound = false;

  get enabled(): boolean {
    return this.soundEnabled;
  }

  setEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    try {
      localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
    } catch {}
  }

  toggle(): boolean {
    this.setEnabled(!this.soundEnabled);
    return this.soundEnabled;
  }

  bindUnlockGesture(target: EventTarget = window): void {
    if (this.unlockBound) return;
    this.unlockBound = true;

    const unlock = (): void => {
      void this.resume();
      target.removeEventListener('pointerdown', unlock);
      target.removeEventListener('keydown', unlock);
      target.removeEventListener('touchstart', unlock);
    };

    target.addEventListener('pointerdown', unlock, { once: false });
    target.addEventListener('keydown', unlock, { once: false });
    target.addEventListener('touchstart', unlock, { once: false });
  }

  play(sfx: Sfx): void {
    if (!this.soundEnabled) return;

    const context = this.ensureContext();
    if (!context) return;
    if (context.state === 'suspended') void context.resume();

    const source = context.createBufferSource();
    source.buffer = this.bufferFor(context, sfx);
    source.connect(context.destination);
    source.start();
  }

  private async resume(): Promise<void> {
    const context = this.ensureContext();
    if (context && context.state === 'suspended') await context.resume();
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;

    const Constructor = resolveAudioContext();
    if (!Constructor) return null;

    try {
      this.context = new Constructor();
    } catch {
      this.context = null;
    }
    return this.context;
  }

  private bufferFor(context: AudioContext, sfx: Sfx): AudioBuffer {
    const cached = this.buffers.get(sfx);
    if (cached) return cached;

    const samples = samplesFor(sfx, context.sampleRate);
    const buffer = context.createBuffer(1, samples.length, context.sampleRate);
    buffer.getChannelData(0).set(samples);
    this.buffers.set(sfx, buffer);
    return buffer;
  }
}

export const audio = new AudioService();
